// dashboard/src/components/credits/TransactionHistory.tsx
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getCreditTransactions, Transaction } from '../../services/creditService';
import { formatDate } from '../../utils/dateUtils';

interface TransactionHistoryProps {
  limit?: number;
}

const TransactionHistory: React.FC<TransactionHistoryProps> = ({ limit = 20 }) => {
  const [page, setPage] = useState(0);
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [transactionType, setTransactionType] = useState<string>('');

  const offset = page * limit;

  const { data, isLoading, error } = useQuery({
    queryKey: ['creditTransactions', limit, offset, startDate, endDate, transactionType],
    queryFn: async () => {
      console.log('Fetching credit transactions with params:', { limit, offset, startDate, endDate, transactionType });
      const result = await getCreditTransactions(limit, offset, startDate, endDate, transactionType);
      console.log('Credit transactions result:', result);
      return result;
    },
    staleTime: 30000,
    refetchOnWindowFocus: true,
  });

  const handleNextPage = () => {
    if (data && data.pagination.has_more) {
      setPage(page + 1);
    }
  };

  const handlePrevPage = () => {
    if (page > 0) {
      setPage(page - 1);
    }
  };

  const handleFilterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // The query will automatically refetch with the new filter values
    setPage(0); // Reset to first page when filtering
  };

  const handleClearFilters = () => {
    setStartDate('');
    setEndDate('');
    setTransactionType('');
    setPage(0);
  };

  const getTransactionTypeLabel = (type: string): string => {
    const typeMap: Record<string, string> = {
      'manual_addition': 'Manual Addition',
      'api_usage': 'API Usage',
      'api_sync': 'Sync API Call',
      'api_async_crawl': 'Async Crawl',
      'refund': 'Refund',
      'adjustment': 'Adjustment',
      'purchase': 'Purchase',
    };

    return typeMap[type] || type;
  };

  const getTransactionTypeColor = (type: string): string => {
    if (type.includes('addition') || type === 'refund' || type === 'purchase') {
      return 'text-primary-600';
    }
    if (type.includes('api') || type.includes('usage')) {
      return 'text-error-600';
    }
    return 'text-gray-600';
  };

  const renderTransactionRow = (transaction: Transaction) => {
    const isPositive = transaction.amount > 0;

    return (
      <tr key={transaction.id} className="hover:bg-gray-50">
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {formatDate(transaction.timestamp)}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm">
          <span className={getTransactionTypeColor(transaction.type)}>
            {getTransactionTypeLabel(transaction.type)}
          </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
          <span className={isPositive ? 'text-primary-600' : 'text-error-600'}>
            {isPositive ? '+' : ''}{transaction.amount.toLocaleString()}
          </span>
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {transaction.description || '-'}
        </td>
        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
          {transaction.related_job_id ? (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
              {data?.job_details[transaction.related_job_id]?.job_type || 'Job'}: {transaction.related_job_id.substring(0, 8)}...
            </span>
          ) : (
            '-'
          )}
        </td>
      </tr>
    );
  };

  if (isLoading && !data) {
    return (
      <div className="bg-white shadow rounded-lg p-6 animate-pulse">
        <div className="h-8 bg-gray-200 rounded w-1/3 mb-4"></div>
        <div className="h-64 bg-gray-200 rounded w-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white shadow rounded-lg p-6 border-l-4 border-error-500">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Transaction History</h3>
        <p className="text-error-500">Error loading transactions. Please try again later.</p>
      </div>
    );
  }

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <div className="px-6 py-4 border-b border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900">Transaction History</h3>
      </div>

      <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
        <form onSubmit={handleFilterSubmit} className="flex flex-wrap gap-4">
          <div>
            <label htmlFor="startDate" className="block text-xs font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              id="startDate"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="input-field"
            />
          </div>

          <div>
            <label htmlFor="endDate" className="block text-xs font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              id="endDate"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="input-field"
            />
          </div>

          <div>
            <label htmlFor="transactionType" className="block text-xs font-medium text-gray-700 mb-1">
              Transaction Type
            </label>
            <select
              id="transactionType"
              value={transactionType}
              onChange={(e) => setTransactionType(e.target.value)}
              className="input-field"
            >
              <option value="">All Types</option>
              <option value="manual_addition">Manual Addition</option>
              <option value="api_usage">API Usage</option>
              <option value="api_sync">Sync API Call</option>
              <option value="api_async_crawl">Async Crawl</option>
              <option value="refund">Refund</option>
              <option value="adjustment">Adjustment</option>
              <option value="purchase">Purchase</option>
            </select>
          </div>

          <div className="flex items-end space-x-2">
            <button
              type="submit"
              className="btn-primary btn-sm"
            >
              Filter
            </button>

            <button
              type="button"
              onClick={handleClearFilters}
              className="btn-secondary btn-sm"
            >
              Clear
            </button>
          </div>
        </form>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Date/Time
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Type
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Amount
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Description
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Related Job
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {(() => {
              console.log('Rendering transactions:', data?.transactions);
              if (!data?.transactions || data.transactions.length === 0) {
                return (
                  <tr>
                    <td colSpan={5} className="px-6 py-4 text-center text-sm text-gray-500">
                      No transactions found
                    </td>
                  </tr>
                );
              } else {
                return data.transactions.map(renderTransactionRow);
              }
            })()}
          </tbody>
        </table>
      </div>

      <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
        <div className="text-sm text-gray-700">
          Showing <span className="font-medium">{offset + 1}</span> to{' '}
          <span className="font-medium">
            {Math.min(offset + limit, data?.pagination.total_count || 0)}
          </span>{' '}
          of <span className="font-medium">{data?.pagination.total_count || 0}</span> transactions
        </div>

        <div className="flex space-x-2">
          <button
            onClick={handlePrevPage}
            disabled={page === 0}
            className="btn-secondary btn-sm"
          >
            Previous
          </button>

          <button
            onClick={handleNextPage}
            disabled={!data?.pagination.has_more}
            className="btn-secondary btn-sm"
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
};

export default TransactionHistory;
